import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Button, IconButton, ToggleSwitch, confirmAlert } from 'components/common';
import { useDispatch } from 'react-redux';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaSyncAlt } from 'react-icons/fa';
import PropTypes from 'prop-types';
import {
  activateOplaCrm,
  deactivateOplaCrm,
  activateLdap,
  deactivateLdap,
  activateGoogleWorkspace,
  deactivateGoogleWorkspace,
  getOplaCrmStatus,
  getLdapStatus,
  getGoogleWorkspaceStatus,
  syncStatusUser
} from 'redux/authorization/userSlice';
import { toast } from 'react-toastify';
const UserPlatformStatus = ({ user, onStatusChange }) => {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(true);
  const [togglingPlatform, setTogglingPlatform] = useState(null);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // platformStates tracks the active status (from API or DB)
  const [platformStates, setPlatformStates] = useState({
    portal: user?.active ?? false,
    opla_crm: user?.opla_crm_account_active ?? false,
    ldap: user?.ldap_account_active ?? false,
    google_workspace: user?.google_workspace_account_active ?? false,
  });
  
  // platformAccounts tracks whether user has account in system
  const [platformAccounts, setPlatformAccounts] = useState({
    portal: !!user?.active, // Portal account existence is based on user record
    opla_crm: !!user?.opla_crm_account_status,
    ldap: !!user?.ldap_account_status,
    google_workspace: !!user?.google_workspace_account_status,
  });

  const fetchPlatformStatuses = useCallback(async () => {
    try {
      setIsFetching(true);
      const statuses = await Promise.all([
        dispatch(getOplaCrmStatus(user?.id)),
        dispatch(getLdapStatus(user?.id)),
        dispatch(getGoogleWorkspaceStatus(user?.id)),
      ]);

      const results = statuses.map(result => result.payload);

      // Update active status states from API response
      setPlatformStates(prev => ({
        ...prev,
        opla_crm: results[0]?.active ?? false,
        ldap: results[1]?.active ?? false,
        google_workspace: results[2]?.active ?? false,
      }));

      // Update account existence states from API response
      setPlatformAccounts({
        portal: true, // Portal account existence is based on user record
        opla_crm: results[0]?.has_account ?? false,
        ldap: results[1]?.has_account ?? false,
        google_workspace: results[2]?.has_account ?? false,
      });

      // Log fetched data for debugging
      console.log('Platform statuses fetched:', {
        portal: { has_account: true, active: user?.active ?? false },
        opla_crm: { has_account: results[0]?.has_account, active: results[0]?.active },
        ldap: { has_account: results[1]?.has_account, active: results[1]?.active },
        google_workspace: { has_account: results[2]?.has_account, active: results[2]?.active }
      });
    } catch (error) {
      console.error('Error fetching platform statuses:', error);
      toast.error('Error loading platform statuses');
    } finally {
      setIsFetching(false);
    }
  }, [dispatch, user?.id, user?.active]);

  useEffect(() => {
    if (user?.id) {
      fetchPlatformStatuses();
      
      // Update Portal state when user active status changes
      setPlatformStates(prev => ({
        ...prev,
        portal: user?.active ?? false
      }));
      
      setPlatformAccounts(prev => ({
        ...prev,
        portal: user?.active ?? false
      }));
    }
  }, [user?.id, user?.active, fetchPlatformStatuses]);


  const handleSyncStatus = async () => {
    confirmAlert({
      title: "Confirmation!",
      message: `Are you sure you want to sync status CRM Opla, LDAP, Google Workspace for user <strong>${user.name}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-primary',
          onClick: async () => {
            try {
              setIsSyncing(true);
              const startTime = Date.now();
              
              const result = await dispatch(syncStatusUser(user.id)).unwrap();
              
              // Ensure loading shows for at least 800ms for better UX
              const elapsed = Date.now() - startTime;
              const minLoadingTime = 800;
              if (elapsed < minLoadingTime) {
                await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsed));
              }
              
              // Handle response with detailed error reporting
              if (result?.data?.results) {
                const results = result.data.results;
                const summary = result.data.summary;
                
                // Show platform-specific errors
                Object.entries(results).forEach(([platform, status]) => {
                  if (!status.success) {
                    const platformName = platform === 'opla_crm' ? 'Opla CRM' 
                                      : platform === 'ldap' ? 'LDAP' 
                                      : 'Google Workspace';
                    toast.error(`✗ ${platformName}: ${status.error}`);
                  }
                });
                
                // Show summary
                if (summary.failed === 0) {
                  toast.success('Platform status sync completed.');
                } else {
                  toast.warning(`⚠ Sync completed: ${summary.successful}/${summary.total_platforms} succeeded, ${summary.failed} failed`);
                }
                
                // Refetch after sync
                setTimeout(() => {
                  fetchPlatformStatuses();
                }, 500);
              }
            } catch (error) {
              console.error('Error syncing user status:', error);
              toast.error(`✗ Error syncing platform statuses: ${error?.message || 'Unknown error'}`);
            } finally {
              setIsSyncing(false);
            }
          }
        },
        {
          label: 'No',
          onClick: () => {} // Do nothing on cancel
        }
      ]
    });
  };

  const handleSyncPlatform = async (key) => {
    if (syncingPlatform === key) return; // Prevent multiple simultaneous requests for same platform
    
    try {
      setSyncingPlatform(key);
      
      let result;
      if (key === 'opla_crm') {
        result = await dispatch(getOplaCrmStatus(user.id));
      } else if (key === 'ldap') {
        result = await dispatch(getLdapStatus(user.id));
      } else if (key === 'google_workspace') {
        result = await dispatch(getGoogleWorkspaceStatus(user.id));
      }

      if (result?.payload) {
        const platformData = result.payload;
        
        // Update platform status
        setPlatformStates(prev => ({
          ...prev,
          [key]: platformData?.active ?? false,
        }));

        // Update platform account existence
        setPlatformAccounts(prev => ({
          ...prev,
          [key]: platformData?.has_account ?? false,
        }));

        const platformName = key === 'opla_crm' ? 'Opla CRM' : key === 'ldap' ? 'LDAP' : 'Google Workspace';
        toast.success(`✓ ${platformName} status synced successfully`);
      }
    } catch (error) {
      console.error(`Error syncing ${key} status:`, error);
      const platformName = key === 'opla_crm' ? 'Opla CRM' : key === 'ldap' ? 'LDAP' : 'Google Workspace';
      toast.error(`✗ Error syncing ${platformName} status`);
    } finally {
      setSyncingPlatform(null);
    }
  };

  const platforms = [
    {
      key: 'portal',
      name: 'Portal',
      icon: '🔐',
      description: 'Access to CRM Portal',
      color: '#667eea',
    },
    {
      key: 'opla_crm',
      name: 'Opla CRM',
      icon: '📊',
      description: 'Opla CRM system access',
      color: '#f093fb',
    },
    {
      key: 'ldap',
      name: 'LDAP',
      icon: '🔗',
      description: 'Domain Controller (Vietnam only)',
      color: '#4facfe',
    },
    {
      key: 'google_workspace',
      name: 'Google Workspace',
      icon: '📧',
      description: 'Google Workspace & Email',
      color: '#fa709a',
    },
  ];

  const handleToggle = async (key) => {
    if (togglingPlatform) return; // Prevent multiple simultaneous requests
    
    const isCurrentlyActive = platformStates[key];
    const hasAccount = platformAccounts[key];
    
    // Don't allow toggling if no account exists
    if (!hasAccount) {
      toast.error('✗ User does not have an account on this platform');
      return;
    }

    try {
      setTogglingPlatform(key);
      
      let result;
      if (key === 'portal') {
        // Portal is just the main user active status - show message
        toast.info('ℹ Portal status is managed through user activation/deactivation');
        setTogglingPlatform(null);
        return;
      } else if (key === 'opla_crm') {
        result = await dispatch(isCurrentlyActive ? deactivateOplaCrm(user.id) : activateOplaCrm(user.id));
      } else if (key === 'ldap') {
        result = await dispatch(isCurrentlyActive ? deactivateLdap(user.id) : activateLdap(user.id));
      } else if (key === 'google_workspace') {
        result = await dispatch(isCurrentlyActive ? deactivateGoogleWorkspace(user.id) : activateGoogleWorkspace(user.id));
      }

      if (result.type && result.type.endsWith('fulfilled')) {
        // Update local state immediately for better UX
        setPlatformStates(prev => ({
          ...prev,
          [key]: !isCurrentlyActive
        }));

        const platformName = key === 'opla_crm' ? 'Opla CRM' : key === 'ldap' ? 'LDAP' : 'Google Workspace';
        const newStatus = !isCurrentlyActive ? 'enabled' : 'disabled';
        toast.success(`✓ ${platformName} ${newStatus} successfully`);
        
        onStatusChange?.({
          [key]: !isCurrentlyActive
        });

        // Refetch to sync with backend
        setTimeout(() => {
          fetchPlatformStatuses();
        }, 1000);
      } else {
        toast.error('✗ Error updating platform access');
        
        // Refetch on error to reset to actual state
        fetchPlatformStatuses();
      }
    } catch (error) {
      console.error('Error toggling platform:', error);
      toast.error(`✗ Error: ${error?.response?.data?.message || error.message}`);

      // Refetch on error to reset to actual state
      fetchPlatformStatuses();
    } finally {
      setTogglingPlatform(null);
    }
  };

  const PlatformCard = ({ platform }) => {
    const isActive = platformStates[platform.key];
    const hasAccount = platformAccounts[platform.key];
    const isTogglingLoading = togglingPlatform === platform.key;
    const isSyncLoading = syncingPlatform === platform.key;
    const canSync = ['opla_crm', 'ldap', 'google_workspace'].includes(platform.key);
    const isPortal = platform.key === 'portal';

    const stateClass = !hasAccount
      ? 'tw-border-slate-200 tw-bg-slate-50/60 tw-opacity-75'
      : isActive
        ? 'tw-border-emerald-200 tw-bg-emerald-50/40'
        : 'tw-border-red-200 tw-bg-red-50/40';

    return (
      <div className={`tw-rounded-lg tw-border tw-p-4 tw-transition-colors ${stateClass}`}>
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className="tw-text-2xl tw-leading-none tw-mt-0.5 tw-select-none">{platform.icon}</div>

          <div className="tw-flex-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <h6 className="tw-mb-0 tw-font-bold tw-text-sm tw-text-slate-800">{platform.name}</h6>
              <span title={platform.description} className="tw-inline-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-100 tw-text-slate-500 tw-text-[10px] tw-cursor-help">?</span>
            </div>
            <p className="tw-text-xs tw-text-slate-500 tw-mb-2 tw-mt-0.5 tw-truncate">{platform.description}</p>

            <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
              {hasAccount
                ? <Badge variant="info" size="sm">Has Account</Badge>
                : <Badge variant="default" size="sm">No Account</Badge>}
              {hasAccount && (isActive
                ? <Badge variant="success" size="sm"><FaCheckCircle size={10} /> Active</Badge>
                : <Badge variant="danger" size="sm"><FaTimesCircle size={10} /> Inactive</Badge>)}
            </div>
          </div>

          {hasAccount && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
              {canSync && (
                <IconButton
                  icon={isSyncLoading ? <FaSpinner className="tw-animate-spin" /> : <FaSyncAlt />}
                  variant="ghost"
                  size="sm"
                  title="Sync account status"
                  disabled={isSyncLoading}
                  onClick={() => handleSyncPlatform(platform.key)}
                />
              )}
              {/* Portal access is managed via user activation/deactivation, so its
                  toggle is read-only here (prevents accidental clicks). */}
              <ToggleSwitch
                value={!!isActive}
                size="sm"
                disabled={isTogglingLoading || isPortal}
                onChange={() => handleToggle(platform.key)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  PlatformCard.propTypes = { platform: PropTypes.object.isRequired };

  return (
    <div>
      {/* Header: user status summary + Sync Now */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <span className="tw-text-sm tw-font-semibold tw-text-slate-700">User status</span>
          <Badge variant={user?.active ? 'success' : 'danger'} size="sm">
            {user?.active ? 'Active' : 'Inactive'}
          </Badge>
          <span className="tw-text-xs tw-text-slate-500">
            {user?.active
              ? 'Can access all enabled platforms'
              : 'Deactivated — all platform access revoked'}
          </span>
        </div>
        <Button variant="primary" size="sm" loading={isSyncing} onClick={handleSyncStatus}>
          Sync Now
        </Button>
      </div>

      <p className="tw-text-xs tw-text-slate-400 tw-mb-4">
        💡 Toggle to enable/disable access — only platforms where the user has an account can be toggled.
      </p>

      {isFetching ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-py-10">
          <FaSpinner className="tw-animate-spin tw-text-blue-500 tw-text-2xl tw-mb-2" />
          <p className="tw-text-sm tw-text-slate-500 tw-mb-0">Loading platform status…</p>
        </div>
      ) : (
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
          {platforms.map(p => (
            <PlatformCard key={p.key} platform={p} />
          ))}
        </div>
      )}
    </div>
  );
};

UserPlatformStatus.propTypes = {
  user: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func,
};

export default UserPlatformStatus;
